import { createReducer, on } from '@ngrx/store';
import * as ListingSortActions from '../listing-sort-store/listing-sort.actions';
import { ListingRequestActions } from './listing-request.actions';
import {
  compactListingValues,
  isEmptyListingValue,
  ListingFilters,
  ListingRequest,
  ListingRequestEntry,
  ListingRequestState
} from './listing-request.model';

export const initialState: ListingRequestState = {};

export function createListingRequest(pageSize: number, scope?: ListingFilters, filters?: ListingFilters): ListingRequest {
  return {
    page: 1,
    pageSize,
    filters: compactListingValues(filters),
    scope: compactListingValues(scope),
    loading: false,
    revalidating: false,
    loaded: false,
    error: undefined,
    count: 0
  };
}

/** Patch the request of an OPENED entry; an entry without one is left alone. */
function patchRequest(
  state: ListingRequestState,
  key: string,
  patch: (request: ListingRequest) => Partial<ListingRequest>
): ListingRequestState {
  const entry = state[key];
  if (!entry?.request) return state;
  return { ...state, [key]: { ...entry, request: { ...entry.request, ...patch(entry.request) } } };
}

function setEntry(state: ListingRequestState, key: string, entry: ListingRequestEntry): ListingRequestState {
  if (entry.sort === undefined && entry.request === undefined) {
    if (!(key in state)) return state;
    const { [key]: _removed, ...rest } = state;
    return rest;
  }
  return { ...state, [key]: entry };
}

/** Whether two compacted value objects hold the same keys and values, whatever their key order. */
function sameListingValues(a: ListingFilters, b: ListingFilters): boolean {
  const keys = Object.keys(a);
  if (keys.length !== Object.keys(b).length) return false;
  return keys.every((key) => key in b && JSON.stringify(a[key]) === JSON.stringify(b[key]));
}

function mergeFilters(current: ListingFilters, changes: ListingFilters): ListingFilters {
  const next: ListingFilters = { ...current };
  for (const [name, value] of Object.entries(changes)) {
    if (isEmptyListingValue(value)) delete next[name];
    else next[name] = value;
  }
  return next;
}

export const listingRequestReducer = createReducer(
  initialState,

  // ── The sort (written by <app-listing>) ───────────────────────────────────
  // A sort change is a change of the result set: an opened listing goes back to page 1, as the
  // listing's own sort event always did.
  on(ListingSortActions.listingSortChanged, (state, { key, sort }) => {
    const entry = state[key];
    return setEntry(state, key, entry?.request ? { sort, request: { ...entry.request, page: 1 } } : { sort });
  }),
  on(ListingSortActions.listingSortCleared, (state, { key }) => {
    const entry = state[key];
    if (!entry?.sort) return state;
    return setEntry(state, key, entry.request ? { request: { ...entry.request, page: 1 } } : {});
  }),

  // ── From the host ─────────────────────────────────────────────────────────
  // A return keeps the page — unless the scope changed while the listing was away (NBO-2508: the
  // "My companies" ids are shared with the order listings and Home), when the stored page may not
  // exist under the new scope. That is the same rule `scopeChanged` applies while it is on screen.
  // A return carrying a hand-off (`filters`, NBO-2509) REPLACES the stored filters with it and goes
  // to page 1 — one `opened`, one request. Replaces, not merges: a hand-off lands on the list
  // filtered to exactly what it names (NBO-2390 — Home's count must be the number the list shows),
  // so filters the user had left there must not narrow it further.
  on(ListingRequestActions.opened, (state, { key, pageSize, scope, filters }) => {
    const entry = state[key] ?? {};
    let request: ListingRequest;
    if (entry.request) {
      const nextScope = compactListingValues(scope);
      const handoff = compactListingValues(filters);
      const handedOff = Object.keys(handoff).length > 0;
      const page = !handedOff && sameListingValues(entry.request.scope, nextScope) ? entry.request.page : 1;
      const nextFilters = handedOff ? handoff : entry.request.filters;
      request = { ...entry.request, scope: nextScope, filters: nextFilters, page, loading: false, revalidating: false };
    } else {
      request = createListingRequest(pageSize, scope, filters);
    }
    return { ...state, [key]: { ...entry, request } };
  }),
  on(ListingRequestActions.closed, (state, { key, forget }) => {
    if (!state[key]) return state;
    if (forget) return setEntry(state, key, {});
    // A request still in flight is cancelled with the subscription — its loading must not survive.
    return patchRequest(state, key, () => ({ loading: false, revalidating: false }));
  }),
  on(ListingRequestActions.scopeChanged, (state, { key, scope, page }) =>
    patchRequest(state, key, () => ({ scope: compactListingValues(scope), page: page ?? 1 }))
  ),
  on(ListingRequestActions.countAdjusted, (state, { key, delta }) =>
    patchRequest(state, key, (request) => ({ count: Math.max(0, request.count + delta) }))
  ),

  // ── From the user ─────────────────────────────────────────────────────────
  on(ListingRequestActions.pageChanged, (state, { key, page, pageSize }) =>
    patchRequest(state, key, () => ({ page, pageSize }))
  ),
  on(ListingRequestActions.filtersChanged, (state, { key, filters }) =>
    patchRequest(state, key, (request) => ({ filters: mergeFilters(request.filters, filters), page: 1 }))
  ),
  on(ListingRequestActions.filtersCleared, (state, { key }) => patchRequest(state, key, () => ({ filters: {}, page: 1 }))),

  // ── From the runner ───────────────────────────────────────────────────────
  on(ListingRequestActions.requestStarted, (state, { key, background }) =>
    patchRequest(state, key, () => ({ loading: true, revalidating: !!background, error: undefined }))
  ),
  on(ListingRequestActions.requestSucceeded, (state, { key, count }) =>
    patchRequest(state, key, () => ({ loading: false, revalidating: false, loaded: true, error: undefined, count }))
  ),
  on(ListingRequestActions.requestFailed, (state, { key, error }) =>
    patchRequest(state, key, () => ({ loading: false, revalidating: false, error }))
  )
);
